import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

/**
 * The pause banner of the wizard (Figma C1, docs/juiscigpt-wizard.md REQ-17):
 * a dark bar above the composer while a running tour shows no note, after
 * "Quitter la visite" or in a later session. "Reprendre" opens the note where
 * the tour was; the close control ends the tour (REQ-10).
 */
export function WizardPauseBanner({
  note,
  onResume,
  onEnd,
}: {
  // The note the tour stands at: 1 to 3, or 3 for the trial.
  note: 1 | 2 | 3;
  onResume: () => void;
  onEnd: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t("Tour paused")}
      className="flex w-full items-center gap-3 rounded-2xl bg-ds-moss-950/90 py-1 pl-4 pr-1 text-sm text-ds-canvas shadow-lg"
    >
      <ol className="flex shrink-0 gap-1" aria-hidden="true">
        {([1, 2, 3] as const).map((step) => (
          <li
            key={step}
            className={cn(
              "h-0.5 w-3 rounded-full",
              step <= note ? "bg-ds-canvas" : "bg-ds-canvas/30"
            )}
          />
        ))}
      </ol>
      <span className="min-w-0 flex-1 truncate">{t("Tour paused")}</span>
      <button
        type="button"
        className="h-10 shrink-0 px-2 font-medium text-ds-canvas/80 hover:text-ds-canvas"
        onClick={onResume}
      >
        {t("Resume")}
      </button>
      <button
        type="button"
        aria-label={t("End the tour")}
        className="flex size-10 shrink-0 items-center justify-center rounded-full text-ds-canvas/80 hover:bg-ds-canvas/10"
        onClick={onEnd}
      >
        <IconX className="size-4" stroke={1.8} />
      </button>
    </section>
  );
}
